// material
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  CircularProgress,
  Button,
  LinearProgress,
  Typography,
  Pagination,
  Stack,
} from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { omitBy } from 'lodash';
import { useSelector } from 'react-redux';

// components
import Scrollbar from '../Scrollbar';
import { UserListHead } from '../../sections/@dashboard/user';

// store
import { supplementaryApi } from '../../store/domains/supplementary';
import { DEFAULT_SUPPLEMENTARY_FORM, PROVIDER_ID } from '../../constants';
import SoDialog from '../Dialogs/SoDialog';
import DeleteSoDialog from '../Dialogs/DeleteSoDialog';
import SupplementaryOfferFilters from './SupplementaryOfferFilters';

const TABLE_HEAD = [
  { id: 'offer_id', label: 'Offer ID', alignRight: false },
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'price', label: 'Price', alignRight: false },
  { id: 'provider_id', label: 'Provider', alignRight: true },
  { id: 'actions', label: 'Actions', alignRight: true },
];

export default function SupplementaryOfferPage() {
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_SUPPLEMENTARY_FORM);
  const [esimFilter, setEsimFilter] = useState(2);
  const [dataSimFilter, setDataSimFilter] = useState(0);
  const [asanImzaFilter, setAsanImzaFilter] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [offerIdSearch, setOfferIdSearch] = useState('');
  const [primaryOfferIdSearch, setPrimaryOfferIdSearch] = useState('');
  const [titleSearch, setTitleSearch] = useState('');

  const { providerId } = useSelector((state) => ({
    providerId: state.auth.providerId,
  }));

  const params = useMemo(
    () =>
      omitBy(
        {
          page: page - 1,
          size: rowsPerPage,
          providerId,
          eSim: esimFilter,
          dataSim: dataSimFilter,
          offerId: offerIdSearch,
          primaryOfferId: primaryOfferIdSearch,
          title: titleSearch,
          asanImza: asanImzaFilter || undefined,
        },
        (v) => v === '' || v === null || v === undefined
      ),
    [
      page,
      rowsPerPage,
      offerIdSearch,
      primaryOfferIdSearch,
      titleSearch,
      esimFilter,
      dataSimFilter,
      asanImzaFilter,
      providerId,
    ]
  );

  const { data, isLoading, isFetching } = supplementaryApi.useGetSupplementaryOffersQuery(params);
  const {
    data: primaryOffers,
    isLoading: isOffersLoading,
    isFetching: isOffersFetching,
  } = supplementaryApi.useGetPrimaryOffersQuery({ size: 100, providerId, eSim: esimFilter });
  const [getSupplementaryOfferById, { isFetching: isOfferDetailsLoading }] =
    supplementaryApi.useLazyGetSupplementaryOfferByIdQuery();
  const [createOffer, { isLoading: isCreating }] = supplementaryApi.useCreateSupplementaryOfferMutation();
  const [updateOffer, { isLoading: isUpdating }] = supplementaryApi.useUpdateSupplementaryOfferMutation();
  const [deleteOffer] = supplementaryApi.useDeleteSupplementaryOfferMutation();

  const offerList = useMemo(() => {
    if (!primaryOffers) return [];
    return primaryOffers.data.flatMap(({ tariffs }) =>
      tariffs.map(({ offer_id: offerId, title }) => ({ value: offerId, label: title }))
    );
  }, [primaryOffers]);

  const handleField = useCallback((field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value })), []);

  const handleSwitch = useCallback(
    (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.checked })),
    []
  );

  const handleOpenDialog = useCallback(
    async (offer = null) => {
      if (!offer) {
        setEditingOffer(null);
        setFormData(DEFAULT_SUPPLEMENTARY_FORM);
        setOpenDialog(true);
        return;
      }

      setEditingOffer(offer);
      setFormData(DEFAULT_SUPPLEMENTARY_FORM);
      setOpenDialog(true);

      try {
        const response = await getSupplementaryOfferById({
          id: offer.offer_id,
          providerId,
          eSim: esimFilter,
        }).unwrap();
        const details = response?.data ?? {};

        setFormData({
          offerId: details.offerId ?? offer.offer_id ?? '',
          title: details.title ?? '',
          nameAz: details.nameAz ?? '',
          nameEng: details.nameEng ?? '',
          nameRu: details.nameRu ?? '',
          price: details.price ?? '',
          providerId: details.providerId ?? providerId ?? '',
          primaryOfferIds: details.primaryOffers?.map(({ offer_id: offerId }) => offerId) ?? [],
          esimFlag: details.esimFlag ?? false,
          dataSimFlag: details.dataSimFlag ?? false,
          asanImzaFlag: details.asanImzaFlag ?? false,
          userType: details.userType ?? '',
        });
      } catch (err) {
        console.error('Failed to load supplementary offer details:', err);
      }
    },
    [esimFilter, getSupplementaryOfferById, providerId]
  );

  const handleCloseDialog = useCallback(() => setOpenDialog(false), []);

  const handleSave = useCallback(async () => {
    try {
      const payload = {
        ...formData,
        userType: formData.asanImzaFlag ? formData.userType || null : null,
      };
      if (editingOffer) {
        await updateOffer({ id: editingOffer.offer_id, eSim: esimFilter, ...payload }).unwrap();
      } else {
        await createOffer({ eSim: esimFilter, ...payload }).unwrap();
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Save error:', err);
    }
  }, [editingOffer, esimFilter, formData, createOffer, updateOffer, handleCloseDialog]);

  const handlers = useMemo(
    () => ({
      handleClose: handleCloseDialog,
      handleSave,
      handleField,
      handleSwitch,
      setFormData,
    }),
    [handleSave, handleField, handleSwitch, handleCloseDialog]
  );

  const dialogState = useMemo(
    () => ({
      formData,
      isEditing: !!editingOffer,
      isCreating,
      isUpdating,
    }),
    [formData, editingOffer, isCreating, isUpdating]
  );

  const handleDeleteClick = useCallback((id) => setDeleteId(id), []);

  const handleDeleteConfirm = useCallback(async () => {
    await deleteOffer(deleteId);
    setDeleteId(null);
  }, [deleteId, deleteOffer]);

  const handleSearchChange = useCallback((field, value) => {
    const setters = {
      offerIdSearch: setOfferIdSearch,
      primaryOfferIdSearch: setPrimaryOfferIdSearch,
      titleSearch: setTitleSearch,
    };
    setters[field](value);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((field, value) => {
    const setters = {
      esimFilter: setEsimFilter,
      dataSimFilter: setDataSimFilter,
      asanImzaFilter: setAsanImzaFilter,
    };
    setters[field](value);
    setPage(1);
  }, []);

  const handleRowsPerPageChange = useCallback((value) => {
    setRowsPerPage(value);
    setPage(1);
  }, []);

  if (isLoading) return <CircularProgress />;

  return (
    <>
      <SupplementaryOfferFilters
        searches={{ offerIdSearch, primaryOfferIdSearch, titleSearch }}
        filters={{ esimFilter, dataSimFilter, asanImzaFilter }}
        rowsPerPage={rowsPerPage}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onNewOffer={handleOpenDialog}
      />
      <Card>
        {(isFetching || isCreating || isUpdating) && <LinearProgress />}
        <Scrollbar>
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <UserListHead headLabel={TABLE_HEAD} rowCount={data?.data?.length || 0} numSelected={0} />
              <TableBody>
                {data?.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No offers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {data?.data?.map((row, idx) => {
                  const { provider_id: rowProviderId, offer_id: offerId, title, price } = row;
                  const providerLabel = PROVIDER_ID.find((p) => p.value === rowProviderId)?.label ?? rowProviderId;
                  return (
                    <TableRow hover key={offerId} tabIndex={0}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{offerId}</TableCell>
                      <TableCell>{title}</TableCell>
                      <TableCell>{price}</TableCell>
                      <TableCell align="right">{providerLabel}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleOpenDialog(row)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDeleteClick(offerId)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
      <Stack padding={2} spacing={2} justifyContent="center">
        {data?.metadata?.pagination?.total_page > 1 && (
          <Pagination
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            count={data?.metadata?.pagination?.total_page}
            page={page}
            color="primary"
            onChange={(_, value) => setPage(value)}
          />
        )}
      </Stack>

      <SoDialog
        open={openDialog}
        state={dialogState}
        handlers={handlers}
        offerList={offerList}
        isOffersLoading={isOffersLoading}
        isOffersFetching={isOffersFetching}
        isOfferDetailsLoading={isOfferDetailsLoading}
      />

      <DeleteSoDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDeleteConfirm} />
    </>
  );
}
